import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper
} from '@mui/material';
import {
  Warning,
  Cancel,
  PhotoCamera
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PhotoCaptureSection from './PhotoCaptureSection';

const RefusalContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  border: `2px solid ${theme.palette.error.main}`,
  backgroundColor: theme.palette.error.light + '10',
}));

const ConfirmDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    backgroundColor: theme.palette.error.main,
    color: 'white',
  },
}));

/**
 * 🔧 ROADMAP COMPONENT - Section de refus avec validation
 * TextArea raison obligatoire + photos justificatives + double validation
 */
const RefusalSection = ({
  refusalReason = '',
  onRefusalReasonChange,
  refusalPhotos = [],
  onRefusalPhotosChange,
  onConfirmRefusal,
  refusalType = 'pickup', // 'pickup' ou 'return'
  userRole = 'owner', // 'owner' ou 'renter'
  loading = false,
  disabled = false
}) => {
  const { t } = useTranslation('refusalSection');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPredefinedReason, setSelectedPredefinedReason] = useState('');
  const [errors, setErrors] = useState([]);

  // Raisons prédéfinies selon le contexte
  const predefinedReasons = {
    pickup: {
      owner: [
        t('predefinedReasons.pickup.owner.equipmentUnavailable'),
        t('predefinedReasons.pickup.owner.technicalIssue'),
        t('predefinedReasons.pickup.owner.renterAbsent'),
        t('predefinedReasons.pickup.owner.dangerousWeather'),
        t('predefinedReasons.pickup.owner.securityIssue'),
        t('predefinedReasons.pickup.owner.other')
      ],
      renter: [
        t('predefinedReasons.pickup.renter.notAsDescribed'),
        t('predefinedReasons.pickup.renter.unsatisfactoryCondition'),
        t('predefinedReasons.pickup.renter.securityIssue'),
        t('predefinedReasons.pickup.renter.lastMinuteChange'),
        t('predefinedReasons.pickup.renter.differentEquipment'),
        t('predefinedReasons.pickup.renter.other')
      ]
    },
    return: {
      owner: [
        t('predefinedReasons.return.owner.damagedEquipment'),
        t('predefinedReasons.return.owner.notCleaned'),
        t('predefinedReasons.return.owner.lateReturn'),
        t('predefinedReasons.return.owner.incompleteEquipment'),
        t('predefinedReasons.return.owner.damageClaimed'),
        t('predefinedReasons.return.owner.other')
      ],
      renter: [
        t('predefinedReasons.return.renter.issueDuringRental'),
        t('predefinedReasons.return.renter.equipmentFailure'),
        t('predefinedReasons.return.renter.earlyReturnSafety'),
        t('predefinedReasons.return.renter.serviceUnsatisfactory'),
        t('predefinedReasons.return.renter.other')
      ]
    }
  };

  /**
   * Valider les données de refus
   */
  const validateRefusal = () => {
    const newErrors = [];

    if (!refusalReason || refusalReason.trim().length < 10) {
      newErrors.push(t('validation.reasonMinLength'));
    }

    if (refusalReason.length > 1000) {
      newErrors.push(t('validation.reasonMaxLength'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  /**
   * Gérer la sélection d'une raison prédéfinie
   */
  const handlePredefinedReasonSelect = (reason) => {
    setSelectedPredefinedReason(reason);
    if (reason === t('predefinedReasons.pickup.owner.other') || 
        reason === t('predefinedReasons.pickup.renter.other') ||
        reason === t('predefinedReasons.return.owner.other') ||
        reason === t('predefinedReasons.return.renter.other')) {
      onRefusalReasonChange('');
    } else {
      onRefusalReasonChange(reason);
    }
  };

  /**
   * Ouvrir la confirmation
   */
  const handleRefuseClick = () => {
    if (validateRefusal()) {
      setShowConfirmDialog(true);
    }
  };

  /**
   * Confirmer le refus
   */
  const handleConfirmRefusal = () => {
    if (validateRefusal()) {
      onConfirmRefusal({
        refusal_reason: refusalReason,
        photos: refusalPhotos,
        type: refusalType,
        user_role: userRole
      });
      setShowConfirmDialog(false);
    }
  };

  /**
   * Obtenir le titre selon le contexte
   */
  const getTitle = () => {
    return t(`titles.${refusalType}.${userRole}`);
  };

  /**
   * Obtenir la description selon le contexte
   */
  const getDescription = () => {
    return t(`descriptions.${refusalType}.${userRole}`);
  };

  const currentReasons = predefinedReasons[refusalType]?.[userRole] || [];

  return (
    <RefusalContainer elevation={3}>
      {/* En-tête avec avertissement */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Warning color="error" fontSize="large" />
        <Box>
          <Typography variant="h6" color="error.main" fontWeight="bold">
            {getTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getDescription()}
          </Typography>
        </Box>
      </Box>

      {/* Alert d'information */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>⚠️ {t('ui.warning')}:</strong> {t('ui.cancellationWarning')}
        </Typography>
      </Alert>

      {/* Erreurs de validation */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Sélection raison prédéfinie */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('ui.predefinedReasonLabel')}</InputLabel>
        <Select
          value={selectedPredefinedReason}
          onChange={(e) => handlePredefinedReasonSelect(e.target.value)}
          disabled={disabled}
        >
          {currentReasons.map((reason, index) => (
            <MenuItem key={index} value={reason}>
              {reason}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Zone de texte pour la raison */}
      <TextField
        fullWidth
        multiline
        rows={4}
        label={t('ui.detailedReasonLabel')}
        placeholder={t('ui.detailedReasonPlaceholder')}
        value={refusalReason}
        onChange={(e) => onRefusalReasonChange(e.target.value)}
        disabled={disabled}
        error={refusalReason.length > 0 && refusalReason.length < 10}
        helperText={t('ui.characterCount', { 
          count: refusalReason.length, 
          min: refusalReason.length < 10 ? t('ui.minimum10') : '' 
        })}
        sx={{ mb: 3 }}
        inputProps={{ maxLength: 1000 }}
      />

      {/* Section photos justificatives */}
      <PhotoCaptureSection
        photos={refusalPhotos}
        onPhotosChange={onRefusalPhotosChange}
        maxPhotos={5}
        readOnly={disabled}
        photoType="refusal_proof"
        optional={true}
        title={t('ui.justificationPhotosTitle')}
        sectionId="refusalPhotos"
      />

      {/* Bouton de refus */}
      <Box mt={3} textAlign="center">
        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={<Cancel />}
          onClick={handleRefuseClick}
          disabled={disabled || loading || refusalReason.length < 10}
          sx={{ minWidth: 200 }}
        >
          {loading ? t('ui.processing') : getTitle()}
        </Button>
      </Box>

      {/* Dialog de confirmation */}
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning />
            {t('dialog.confirmRefusalTitle')}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              ⚠️ {t('dialog.irreversibleAction')}
            </Typography>
            <Typography variant="body2">
              {t('dialog.cancellationWarning')}
            </Typography>
          </Alert>

          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('dialog.operationType')}:
            </Typography>
            <Chip 
              label={`${refusalType.toUpperCase()} - ${userRole.toUpperCase()}`} 
              color="error" 
              variant="outlined"
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('dialog.refusalReason')}:
            </Typography>
            <Typography variant="body2" sx={{ 
              mt: 1, 
              p: 2, 
              backgroundColor: 'grey.100', 
              borderRadius: 1,
              maxHeight: 100,
              overflow: 'auto'
            }}>
              {refusalReason}
            </Typography>
          </Box>

          {refusalPhotos.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('dialog.justificationPhotos')}:
              </Typography>
              <Typography variant="body2">
                {t('dialog.photosCount', { count: refusalPhotos.length })}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t('dialog.confirmationQuestion')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={() => setShowConfirmDialog(false)}
            disabled={loading}
          >
            {t('dialog.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRefusal}
            disabled={loading}
            startIcon={<Cancel />}
          >
            {loading ? t('ui.processing') : t('dialog.confirmRefusal')}
          </Button>
        </DialogActions>
      </ConfirmDialog>
    </RefusalContainer>
  );
};

export default RefusalSection;
